import type { PortfolioDetail } from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { PortfolioCaseCard, usePortfoliosQuery } from '@/entities/portfolio';
import { portfolioListPath } from '@/shared/config';

/** 상세 아래에 싣는 다른 사례 수 — 데스크톱 한 줄. */
const RELATED_COUNT = 3;

interface PortfolioRelatedProps {
  readonly detail: PortfolioDetail;
}

/**
 * 다른 사례 — 지금 사례의 첫 카테고리 목록 첫 페이지에서 지금 사례를 빼고 최대 세 장.
 * 부가 영역이라 불러오는 중·실패·0개면 자리를 남기지 않고 생략한다.
 */
export const PortfolioRelated = ({ detail }: PortfolioRelatedProps): ReactElement | null => {
  const categoryId = detail.categories[0]?.id;
  const query = usePortfoliosQuery({ categoryId, page: 1 });
  const items = (query.data?.items ?? [])
    .filter((item) => item.id !== detail.id)
    .slice(0, RELATED_COUNT);
  if (items.length === 0) return null;
  return (
    <section aria-labelledby="portfolio-related-heading">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-base text-muted-foreground sm:text-lg">More Cases</p>
          <h2
            id="portfolio-related-heading"
            className="text-[2rem] leading-tight font-bold tracking-[-0.03em] sm:text-[2.75rem]"
          >
            다른 사례
          </h2>
        </div>
        <Link
          to={portfolioListPath({ categoryId })}
          className="inline-flex h-12 items-center gap-2 self-start rounded-full border border-foreground px-6 text-[15px] font-medium transition-colors hover:bg-foreground hover:text-background sm:self-auto"
        >
          전체 보기
          <Plus aria-hidden strokeWidth={1.75} className="size-4" />
        </Link>
      </div>
      <ul className="mt-10 grid grid-cols-1 gap-x-6 gap-y-14 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <li key={item.id} className="min-w-0">
            <PortfolioCaseCard
              item={item}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};
